import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import DropdownSelector from '../DropdownSelector/DropdownSelector';
import SearchBar from '../SearchBar/SearchBar';
import GenericTooltip from '../GenericTooltip/GenericTooltip';
import { useConstants } from '../../constant/constantProvider';
import './TalentPulseFilterBar.css';

const TalentPulseFilterBar = ({
    type = 'all',
    onToggleType = () => { },
    profileTypes = [
        { id: 'all', label: 'All' },
        { id: 'external', label: 'External' }
    ],
    roles = [],
    selectedRole = null,
    onRoleChange = () => { },
    companies = [],
    selectedCompany = null,
    onCompanyChange = () => { },
    regions = [],
    selectedRegion = null,
    onRegionChange = () => { },
    states = [],
    selectedState = null,
    onStateChange = () => { },
    territories = [],
    selectedTerritory = null,
    onTerritoryChange = () => { },
    sort = 'latest',
    onSortChange = () => { },
    onCompareClick = () => { },
    compareDisabled = false,
    compareCount = 0,
    isProfilesHidden = false,
    onToggleProfilesClick = () => { },
    isGridView = false,
    onToggleGridView = () => { },
    candidateCount = 0,
    onClearFilters = () => { },
    // Candidates header props
    isCandidateProfilesSelected = false,
    profileSearchQuery = '',
    onProfileSearchChange = () => { },
    onProfileSearchClear = () => { },
    totalProfilesCount = null,
    searchLower = '',
    visibleCandidatesCount = 0,
    isProfilesLoading = false,
    filtersDisabled = false,
    isCompareActive = false,
    onResetClick = () => { },
}) => {
    const constants = useConstants();
    const isControlDisabled = isProfilesLoading || filtersDisabled;
    const [showMobileFilterModal, setShowMobileFilterModal] = useState(false);

    // Draft states for mobile filter modal (only applied on "Apply" click)
    const [draftType, setDraftType] = useState(type);
    const [draftRole, setDraftRole] = useState(selectedRole);
    const [draftCompany, setDraftCompany] = useState(selectedCompany);
    const [draftRegion, setDraftRegion] = useState(selectedRegion);
    const [draftState, setDraftState] = useState(selectedState);
    const [draftTerritory, setDraftTerritory] = useState(selectedTerritory);
    const [draftSort, setDraftSort] = useState(sort);

    const getProfileTypeLabel = (pt) => {
        if (pt.id === 'all') return constants.ALL || pt.label;
        if (pt.id === 'external') return constants.EXTERNAL || pt.label;
        return pt.label;
    };

    const handleOpenMobileModal = () => {
        if (isProfilesHidden && onToggleProfilesClick) {
            onToggleProfilesClick();
        }
        setDraftType(type);
        setDraftRole(selectedRole);
        setDraftCompany(selectedCompany);
        setDraftRegion(selectedRegion);
        setDraftState(selectedState);
        setDraftTerritory(selectedTerritory);
        setDraftSort(sort);
        setShowMobileFilterModal(true);
    };

    const handleApplyMobileFilters = () => {
        if (isProfilesHidden && onToggleProfilesClick) {
            onToggleProfilesClick();
        }
        onToggleType(draftType);
        onRoleChange(draftRole);
        onCompanyChange(draftCompany);
        onRegionChange(draftRegion);
        onStateChange(draftState);
        onTerritoryChange(draftTerritory);
        onSortChange(draftSort);
        setShowMobileFilterModal(false);
    };

    // Helper to map strings to {id, name} for DropdownSelector
    const mapToItems = (arr) => arr.map(item => ({ id: item, name: item }));

    // Count active filter selections
    const activeFiltersCount = [
        selectedRole,
        selectedCompany,
        selectedRegion,
        selectedState,
        selectedTerritory,
    ].filter(Boolean).length;

    const compareDisabledHint = constants.COMPARE_DISABLED_HINT || "Select 2 or more candidates to compare";

    return (
        <>
            <div className="tpf-sticky-block">
                {/* Row 1: Filter bar */}
                <div className="talent-pulse-filter-bar">
                    {/* Desktop Left Dropdowns */}
                    <div
                        className={`psf-left desktop-only-filters${isControlDisabled ? ' tpf-is-loading' : ''}`}
                        onClickCapture={() => {
                            if (isProfilesHidden && onToggleProfilesClick) {
                                onToggleProfilesClick();
                            }
                        }}
                    >
                        {profileTypes && profileTypes.length > 0 && (
                            <div className="seg">
                                {profileTypes.map(pt => (
                                    <button
                                        key={pt.id}
                                        className={type === pt.id ? 'on' : ''}
                                        onClick={() => onToggleType(pt.id)}
                                        disabled={isControlDisabled}
                                    >
                                        {getProfileTypeLabel(pt)} {pt.count !== undefined && `(${pt.count})`}
                                    </button>
                                ))}
                            </div>
                        )}

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_ROLES || "All roles" }}
                            items={mapToItems(roles)}
                            selected={selectedRole}
                            onChange={onRoleChange}
                            showTriggerIcon={false}
                            type="selected-cross"
                            searchable
                            disabled={isControlDisabled}
                        />

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_COMPANIES || "All companies" }}
                            items={mapToItems(companies)}
                            selected={selectedCompany}
                            onChange={onCompanyChange}
                            showTriggerIcon={false}
                            type="selected-cross"
                            searchable
                            disabled={isControlDisabled}
                        />

                        {/* <DropdownSelector
                            placeholder={{ name: constants.ALL_REGIONS || "All regions" }}
                            items={mapToItems(regions)}
                            selected={selectedRegion}
                            onChange={onRegionChange}
                            showTriggerIcon={false}
                            type="selected-cross"
                            searchable
                            disabled={isControlDisabled}
                        /> */}

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_STATES || "All states" }}
                            items={mapToItems(states)}
                            selected={selectedState}
                            onChange={onStateChange}
                            showTriggerIcon={false}
                            type="selected-cross"
                            searchable
                            disabled={isControlDisabled}
                        />

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_TERRITORIES || "All territories" }}
                            items={mapToItems(territories)}
                            selected={selectedTerritory}
                            onChange={onTerritoryChange}
                            showTriggerIcon={false}
                            type="selected-cross"
                            searchable
                            disabled={isControlDisabled}
                        />

                        <DropdownSelector
                            placeholder={{ name: constants.SORT_LATEST || "Sort: Latest" }}
                            items={[
                                { id: 'latest', name: constants.SORT_LATEST || 'Sort: Latest' },
                                { id: 'az', name: constants.SORT_AZ || 'Sort: A–Z' }
                            ]}
                            selected={sort}
                            onChange={onSortChange}
                            showTriggerIcon={false}
                            itemConfig={{ getValue: (item) => item.id }}
                            disabled={isControlDisabled}
                        />
                    </div>

                    {/* Mobile Top Controls Row */}
                    <div className="tpf-mobile-header-row mobile-only-filters">
                        <div className="tpf-mobile-top-bar">
                            {profileTypes && profileTypes.length > 0 && (
                                <div className="seg">
                                    {profileTypes.map(pt => (
                                        <button
                                            key={pt.id}
                                            className={type === pt.id ? 'on' : ''}
                                            onClick={() => onToggleType(pt.id)}
                                            disabled={isControlDisabled}
                                        >
                                            {getProfileTypeLabel(pt)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <button
                                type="button"
                                className={`btn ghost tpf-filters-trigger ${activeFiltersCount > 0 ? 'has-active' : ''}`}
                                onClick={handleOpenMobileModal}
                                disabled={isControlDisabled}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                                </svg>
                                {constants.FILTERS || "Filters"} {activeFiltersCount > 0 && <span className="tpf-badge">{activeFiltersCount}</span>}
                            </button>

                            <GenericTooltip
                                tooltipText={compareDisabled ? compareDisabledHint : ""}
                                tooltipPosition="bottom"
                                className="tpf-compare-tooltip-wrapper"
                            >
                                <button
                                    type="button"
                                    className="btn tpf-compare-btn"
                                    disabled={compareDisabled}
                                    onClick={onCompareClick}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                        <path d="M4 6h7M4 12h7M4 18h7M20 6h-3M20 12h-3M20 18h-3M15 3v18" />
                                    </svg>
                                    {constants.COMPARE || "Compare"} {compareCount > 0 && <span className="tpf-count-bubble">{compareCount}</span>}
                                </button>
                            </GenericTooltip>

                            {(isCompareActive || compareCount > 0) && (
                                <button
                                    type="button"
                                    className="btn tpf-reset-btn-red tpf-mobile-reset-btn"
                                    onClick={onResetClick}
                                    disabled={isProfilesLoading}
                                    title="Reset comparison & clear selection"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                    {constants.RESET || "Reset"}
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn ghost icon-only tpf-eye-btn"
                                onClick={onToggleProfilesClick}
                                aria-label={isProfilesHidden ? (constants.SHOW_PROFILES || "Show profiles") : (constants.HIDE_PROFILES || "Hide profiles")}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                    {isProfilesHidden ? (
                                        <>
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </>
                                    ) : (
                                        <>
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Desktop Right Action Buttons */}
                    <div className={`psf-right desktop-only-filters${isProfilesLoading ? ' tpf-is-loading' : ''}`}>
                        <GenericTooltip
                            tooltipText={compareDisabled ? compareDisabledHint : ""}
                            tooltipPosition="bottom"
                            className="tpf-compare-tooltip-wrapper"
                        >
                            <button
                                className="btn"
                                disabled={compareDisabled || isProfilesLoading}
                                onClick={onCompareClick}
                                style={(compareDisabled || isProfilesLoading) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                    <path d="M4 6h7M4 12h7M4 18h7M20 6h-3M20 12h-3M20 18h-3M15 3v18" />
                                </svg>
                                {constants.COMPARE || "Compare"} {compareCount > 0 && <span className="tpf-count-bubble">{compareCount}</span>}
                            </button>
                        </GenericTooltip>

                        {(isCompareActive || compareCount > 0) ? (
                            <button
                                className="btn tpf-reset-btn-red"
                                onClick={onResetClick}
                                disabled={isProfilesLoading}
                                title="Reset comparison & clear selection"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                    <path d="M3 3v5h5" />
                                </svg>
                                {constants.RESET || "Reset"}
                            </button>
                        ) : (
                            <button className="btn ghost" onClick={onToggleGridView} disabled={isProfilesHidden || isProfilesLoading}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="15" height="15">
                                    {isGridView ? (
                                        <>
                                            <path d="M4 14h6v6M10 14L3 21" />
                                            <path d="M20 10h-6V4M14 10l7-7" />
                                        </>
                                    ) : (
                                        <>
                                            <path d="M15 3h6v6M21 3l-7 7" />
                                            <path d="M9 21H3v-6M3 21l7-7" />
                                        </>
                                    )}
                                </svg>
                                {isGridView ? (constants.SEE_LESS || "See less") : (constants.SEE_ALL || "See all")}
                            </button>
                        )}

                        <button className="btn ghost" onClick={onToggleProfilesClick} disabled={isProfilesLoading}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                {isProfilesHidden ? (
                                    <path d="M6 9l6 6 6-6" />
                                ) : (
                                    <path d="M18 15l-6-6-6 6" />
                                )}
                            </svg>
                            {isProfilesHidden ? (constants.SHOW_PROFILES || "Show profiles") : (constants.HIDE_PROFILES || "Hide profiles")}
                        </button>
                    </div>
                </div>

                {/* Row 2: Candidates count + search — only when profiles panel is visible */}
                {isCandidateProfilesSelected && !isProfilesHidden && (
                    <div className="tp-candidates-header">
                        {!isProfilesLoading && (
                            <span className="tp-candidates-count">
                                CANDIDATES · {searchLower ? visibleCandidatesCount : (totalProfilesCount ?? visibleCandidatesCount)}
                            </span>
                        )}
                        {isProfilesLoading && (
                            <span className="tp-candidates-count tp-candidates-count--loading" aria-label="Loading candidates">
                                <span className="tp-count-shimmer" />
                            </span>
                        )}
                        <div className="tp-candidates-header-actions">
                            <SearchBar
                                value={profileSearchQuery}
                                onChange={onProfileSearchChange}
                                onClear={onProfileSearchClear}
                                placeholder="Search by name"
                                className="tp-candidates-search-bar"
                                disabled={isControlDisabled}
                                infoNote={
                                    profileSearchQuery.trim().length > 0 && profileSearchQuery.trim().length < 3
                                        ? 'Type at least 3 characters to search'
                                        : null
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Filter Modal — always a centered dialog, rendered via portal above sidebar */}
            <Modal
                open={showMobileFilterModal}
                onClose={() => setShowMobileFilterModal(false)}
                className="tpf-mobile-modal"
            >
                <div className="tpf-modal-handle" />
                <div className="tpf-modal-header">
                    <h3>{constants.FILTERS || "Filters"}</h3>
                    <button
                        type="button"
                        className="tpf-modal-close"
                        onClick={() => setShowMobileFilterModal(false)}
                    >
                        ✕
                    </button>
                </div>

                <div className="tpf-modal-body">
                    {/* CANDIDATE TYPE */}
                    <div className="tpf-modal-section">
                        <label className="tpf-modal-label">{constants.CANDIDATE_TYPE || "CANDIDATE TYPE"}</label>
                        <div className="seg tpf-seg-full">
                            {profileTypes.map(pt => (
                                <button
                                    key={pt.id}
                                    className={draftType === pt.id ? 'on' : ''}
                                    onClick={() => setDraftType(pt.id)}
                                >
                                    {getProfileTypeLabel(pt)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ROLE */}
                    {roles.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.ROLE || "ROLE"}</label>
                            <div className="tpf-pills-grid">
                                {roles.map(r => (
                                    <button
                                        key={r}
                                        type="button"
                                        className={`tpf-pill ${draftRole === r ? 'is-selected' : ''}`}
                                        onClick={() => setDraftRole(draftRole === r ? null : r)}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* COMPANY */}
                    {companies.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.COMPANY || "COMPANY"}</label>
                            <div className="tpf-pills-grid">
                                {companies.map(c => (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`tpf-pill ${draftCompany === c ? 'is-selected' : ''}`}
                                        onClick={() => setDraftCompany(draftCompany === c ? null : c)}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}




                    {states.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.STATE || "STATE"}</label>
                            <div className="tpf-pills-grid">
                                {states.map(s => (
                                    <button
                                        key={s}
                                        type="button"
                                        className={`tpf-pill ${draftState === s ? 'is-selected' : ''}`}
                                        onClick={() => setDraftState(draftState === s ? null : s)}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TERRITORY */}
                    {territories.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.TERRITORY || "TERRITORY"}</label>
                            <div className="tpf-pills-grid">
                                {territories.map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`tpf-pill ${draftTerritory === t ? 'is-selected' : ''}`}
                                        onClick={() => setDraftTerritory(draftTerritory === t ? null : t)}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SORT BY */}
                    <div className="tpf-modal-section">
                        <label className="tpf-modal-label">{constants.SORT_BY || "SORT BY"}</label>
                        <div className="seg tpf-seg-full">
                            <button
                                className={draftSort === 'latest' ? 'on' : ''}
                                onClick={() => setDraftSort('latest')}
                            >
                                {constants.LATEST || "Latest"}
                            </button>
                            <button
                                className={draftSort === 'az' ? 'on' : ''}
                                onClick={() => setDraftSort('az')}
                            >
                                A–Z
                            </button>
                        </div>
                    </div>
                </div>

                {/* Modal Footer: Close + Apply */}
                <div className="tpf-modal-footer">
                    <button
                        type="button"
                        className="tpf-clear-btn"
                        onClick={() => {
                            setShowMobileFilterModal(false);
                        }}
                    >
                        {constants.CLOSE || "Close"}
                    </button>
                    <button
                        type="button"
                        className="tpf-apply-btn"
                        onClick={handleApplyMobileFilters}
                    >
                        {constants.APPLY || "Apply"}
                    </button>
                </div>

            </Modal>
        </>
    );
};

export default TalentPulseFilterBar;
